import type { OutputData } from './helpers.ts';

function csvEscape(v: string | number): string {
  const s = typeof v === 'number' ? String(v) : v;
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function row(values: (string | number)[]): string {
  return values.map(csvEscape).join(',');
}

export function buildCsv(data: OutputData): string {
  const lines: string[] = [];

  // Header block
  lines.push(row(['Job Name', data.submission.job_name]));
  lines.push(row(['Source PDF', data.submission.source_pdf]));
  lines.push(row(['Prepared By', data.preparedBy]));
  lines.push(row(['Generated', data.generatedAt.toISOString()]));
  lines.push('');

  // Civil section
  lines.push(row(['CIVIL TAKEOFF']));
  if (data.civil && data.civil.itemRows.length > 0) {
    lines.push(row(['Page', 'Kind', 'Label', 'Quantity', 'Unit']));
    for (const r of data.civil.itemRows) {
      const qty = r.unit === 'EA' ? r.quantity : Number(r.quantity.toFixed(2));
      lines.push(row([r.pageLabel, r.kind, r.label, qty, r.unit]));
    }
    lines.push('');
    lines.push(row(['Civil Totals']));
    lines.push(row(['Linear (LF)', Number(data.civil.totals.linearLF.toFixed(2))]));
    lines.push(row(['Area (SF)', Number(data.civil.totals.polygonSF.toFixed(2))]));
    lines.push(row(['Count (EA)', data.civil.totals.count]));
  } else {
    lines.push(row(['(no civil takeoff data)']));
  }
  lines.push('');

  // Rebar lines
  lines.push(row(['REBAR LINE ITEMS']));
  if (data.rebar && data.rebar.lineRows.length > 0) {
    lines.push(
      row([
        'Segment',
        'Line #',
        'Material',
        'Function',
        'Size',
        'Class',
        'Qty',
        'Mult',
        'Unit Length (ft)',
        'Total Length (ft)',
        'Weight (lbs)',
      ]),
    );
    for (const r of data.rebar.lineRows) {
      lines.push(
        row([
          r.segmentName,
          r.lineNumber,
          r.material,
          r.function,
          r.size,
          r.class,
          r.quantity,
          r.multiplier,
          Number(r.unitLengthFt.toFixed(2)),
          Number(r.totalLengthFt.toFixed(2)),
          Number(r.totalWeightLbs.toFixed(2)),
        ]),
      );
    }
    lines.push('');

    // Weight summary by size
    lines.push(row(['REBAR WEIGHT SUMMARY']));
    lines.push(row(['Size', 'Lbs', 'Tons', 'Bar Count', 'Total LF']));
    for (const e of data.rebar.weightSummary) {
      lines.push(
        row([
          e.size.toString(),
          Number(e.total.toFixed(2)),
          Number(e.tons.toFixed(3)),
          e.barCount,
          Number(e.totalLinealFeet.toFixed(2)),
        ]),
      );
    }
    lines.push(row(['TOTAL', Number(data.rebar.totalLbs.toFixed(2)), Number(data.rebar.totalTons.toFixed(3)), '', '']));
  } else {
    lines.push(row(['(no rebar takeoff data)']));
  }

  return lines.join('\r\n');
}

export function downloadCsv(data: OutputData): void {
  const csv = buildCsv(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFor(data, 'csv');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function filenameFor(data: OutputData, ext: 'csv' | 'pdf'): string {
  const safe = data.submission.job_name.replace(/[^a-z0-9-_]+/gi, '_').slice(0, 60) || 'estimate';
  const stamp = data.generatedAt.toISOString().slice(0, 10);
  return `${safe}_${stamp}.${ext}`;
}
